import React from 'react';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Form, DatePicker } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import Table from '@/components/ui/table/Table';
import { Button } from '@/components/ui/Button';
import { Input, TextArea } from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { usePerformance } from '../hooks/usePerformance';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import {
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ErrorBlock,
} from '@/pages/inside/tenant/style';

const Performance = () => {
    const {
        selectedUserId,
        setSelectedUserId,
        notesList,
        tableData,
        columns,
        canCreate,
        createModalOpen,
        openCreateModal,
        createForm,
        onSubmitCreate,
        closeCreateModal,
        createNotePending,
        employeeOptions,
        notesError,
        downloadPdf,
    } = usePerformance();

    return (
        <ListBodyLayout
            title="Performance Notes"
            description="View and add performance notes for employees"
            actions={
                <Container $display="flex" $alignItems="center" $gap="12px">
                    {selectedUserId && tableData.length > 0 && (
                        <Button
                            $primary={false}
                            $size="14px"
                            $padding="10px 16px"
                            onClick={() => {
                                const label =
                                    employeeOptions.find((o) => o.value === selectedUserId)?.label ||
                                    '';
                                downloadPdf(label);
                            }}
                        >
                            Download PDF
                        </Button>
                    )}
                    {canCreate && (
                        <Button
                            $primary
                            $blackOnLight
                            icon={<PlusOutlined />}
                            onClick={openCreateModal}
                            $padding="10px 20px"
                            $size="14px"
                        >
                            Add Note
                        </Button>
                    )}
                </Container>
            }
        >
            {notesError && selectedUserId && (
                <ErrorBlock>
                    Unable to load performance notes. Check that the HR API (GET /api/v1/hr/performance/:userId) is
                    available.
                </ErrorBlock>
            )}

            <Container $display="flex" $alignItems="center" $gap="12px" $flexWrap="wrap" $marginBottom="24px">
                <span style={{ fontWeight: 500 }}>Employee:</span>
                <Select
                    placeholder="Select employee"
                    value={selectedUserId || undefined}
                    onChange={setSelectedUserId}
                    options={employeeOptions}
                    showSearch
                    optionFilterProp="label"
                    style={{ minWidth: 280 }}
                    allowClear
                />
            </Container>

            {selectedUserId && (
                <Table
                    title=""
                    columns={columns}
                    dataSource={tableData}
                    showSearch={false}
                    showFilter={false}
                    showMore={false}
                    hideToolbar
                    pagination={tableData.length > 10}
                    pageSize={10}
                    $topMargin={false}
                    $radius={12}
                />
            )}
            {selectedUserId && notesList.length === 0 && !notesError && (
                <Container $padding="24px" $display="flex" $justifyContent="center" $alignItems="center">
                    No performance notes yet. Add one with the button above.
                </Container>
            )}
            {!selectedUserId && (
                <Container $padding="24px" $display="flex" $justifyContent="center" $alignItems="center">
                    Select an employee to view their performance notes.
                </Container>
            )}

            <AppModal
                size="md"
                title="Add Performance Note"
                open={createModalOpen}
                onCancel={closeCreateModal}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody>
                    <Form form={createForm} layout="vertical">
                        <Form.Item
                            name="userId"
                            label="Employee"
                            rules={[{ required: true, message: 'Select employee' }]}
                        >
                            <Select
                                placeholder="Select employee"
                                options={employeeOptions}
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                        <Form.Item
                            name="period"
                            label="Period"
                            rules={[{ required: true, message: 'Select period' }]}
                        >
                            <DatePicker.RangePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                        </Form.Item>
                        <Form.Item
                            name="project_deliveries"
                            label="Project deliveries (%)"
                            rules={[{ required: true, message: 'Enter project deliveries percentage' }]}
                        >
                            <Input
                                $type="number"
                                placeholder="0–100"
                                $maxWidth="100%"
                                min={0}
                                max={100}
                            />
                        </Form.Item>
                        <Form.Item
                            name="attendanceMetric"
                            label="Attendance (%)"
                            rules={[{ required: true, message: 'Enter attendance percentage' }]}
                        >
                            <Input
                                $type="number"
                                placeholder="0–100"
                                $maxWidth="100%"
                                min={0}
                                max={100}
                            />
                        </Form.Item>
                        <Form.Item
                            name="behaviour"
                            label="Behaviour (%)"
                            rules={[{ required: true, message: 'Enter behaviour percentage' }]}
                        >
                            <Input
                                $type="number"
                                placeholder="0–100"
                                $maxWidth="100%"
                                min={0}
                                max={100}
                            />
                        </Form.Item>
                        <Form.Item
                            name="extra_caricullar_activities"
                            label="Extra-curricular activities (%)"
                            rules={[{ required: true, message: 'Enter extra-curricular activities percentage' }]}
                        >
                            <Input
                                $type="number"
                                placeholder="0–100"
                                $maxWidth="100%"
                                min={0}
                                max={100}
                            />
                        </Form.Item>
                        <Form.Item name="note" label="Note" rules={[{ required: true, message: 'Enter note' }]}>
                            <TextArea $minHeight="96px" placeholder="e.g. Strong sprint delivery." />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button $primary={false} $size="16px" $padding="8px 16px" onClick={closeCreateModal}>
                        Cancel
                    </Button>
                    <Button $primary $blackOnLight $size="16px" $padding="8px 16px" onClick={onSubmitCreate} loading={createNotePending}>
                        Add
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>
        </ListBodyLayout>
    );
};

export default Performance;
